import { AvailableLanguages } from '@repo/toolkit/language-types'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createContext, type PropsWithChildren, useEffect, useState } from 'react'

import i18n from '../../../i18n'
import { terminateSession } from '../../api/auth/logout'
import { getMe } from '../../api/user/get-me'
import LoadingScreen from '../../components/elements/loading/loading-screen'
import { ThemeName } from '../../components/hoc/with-theme'
import { RoleEnum } from '../types/enums/users/role.enum'
import { User } from '../types/models/users/user'
import { Merge } from '../types/utils'
import { showAxiosError } from '../utils/functions/misc.functions'

export type AuthContextType = {
  user?: User
  isLoading: boolean
  userRole?: RoleEnum
  setUser: (user: User) => void
  logout: () => Promise<void>
  invalidateUser: () => Promise<void>
}

// type to be used in casting when we know the user is authenticated
export type AuthenticatedUser = Merge<AuthContextType, { user: User; isLoading: false }>

const userRoleThemeMapping: Record<RoleEnum, ThemeName> = {
  ADMIN: 'admin',
  PCR: 'admin',
  STAFF: 'staff',
  Ol: 'ol',
  CLIENT: 'client',
  ACCOUNTANT: 'admin',
  SYSTEM_ADMIN: 'admin'
}

export const AuthContext = createContext<AuthContextType>({
  isLoading: true,
  setUser: () => {},
  logout: () => Promise.resolve(),
  invalidateUser: () => Promise.resolve()
})

const AuthProvider = ({ children }: PropsWithChildren) => {
  const queryClient = useQueryClient()
  const [user, setUser] = useState<User>()
  const [userRole, setUserRole] = useState<RoleEnum>()

  // This mapping represents the available languages for each role

  const userLanguageMapping: Record<
    RoleEnum,
    { languages: AvailableLanguages[]; defaultLanguage: AvailableLanguages }
  > = {
    ADMIN: { languages: ['fr'], defaultLanguage: 'fr' },
    PCR: { languages: ['fr'], defaultLanguage: 'fr' },
    STAFF: { languages: ['en', 'fr'], defaultLanguage: 'fr' },
    Ol: { languages: ['en'], defaultLanguage: 'en' },
    CLIENT: { languages: ['fr'], defaultLanguage: 'fr' },
    ACCOUNTANT: { languages: ['fr'], defaultLanguage: 'fr' },
    SYSTEM_ADMIN: { languages: ['en'], defaultLanguage: 'en' }
  }

  const { isFetching } = useQuery({
    queryKey: ['user'],
    queryFn: async () => {
      const data = await getMe()
      setUser(data)
      return data
    }
  })

  const { mutateAsync: logoutMutation } = useMutation({
    mutationFn: terminateSession,
    onSuccess() {
      setUser(undefined)
      setUserRole(undefined)
    },
    onError: showAxiosError
  })

  const handleSetUser = (user?: User) => {
    setUser(user)
  }

  const handleLogout = async () => {
    await logoutMutation()
  }

  useEffect(() => {
    // Check for user theme and set it
    if (user) {
      setUserRole(user.role)
      document.querySelector('html')?.setAttribute('data-theme', userRoleThemeMapping[user.role])
      const availableLanguages = userLanguageMapping[user.role]
      // Check if the local storage language is in the available languages for the user role
      const storedLanguage = localStorage.getItem('language') as AvailableLanguages
      const userLanguage = availableLanguages.languages.includes(storedLanguage)
        ? storedLanguage
        : availableLanguages.defaultLanguage

      i18n.changeLanguage(userLanguage)
    }
  }, [user])

  if (isFetching) return <LoadingScreen />

  return (
    <AuthContext.Provider
      value={{
        user,
        userRole,
        isLoading: isFetching,
        setUser: handleSetUser,
        invalidateUser: () => queryClient.invalidateQueries({ queryKey: ['user'] }),
        logout: () => handleLogout()
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export default AuthProvider
